import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';

import { MAP } from '../../Constants/Routes';
import AuthService from '../../Services/AuthService';
import LocalStorageService from '../../Services/LocalStorageService';
import RequestBuilder from '../../Lib/Requests';
import BrandingContext from '../../Context/BrandingContext';

import AcceptTermsAndConditionsModal from './AcceptTermsAndConditionsModal';
import LoginDisplay from './LoginDisplay';
import MFADisplay from './MFADisplay';

export default memo(LoginContainer);

const initState = {
  alertColor: '',
  alertMessage: '',
  alertText: '',
  attemptCount: 3,
  challenge: '',
  clientToken: '',
  email: '',
  error: '',
  hidePassword: true,
  loginRequestPending: false,
  password: '',
  showAcceptTermsModal: false,
  showAlert: false,
  showError: false,
  showMFA: false,
  termsModal: false,
  twoFactorError: '',
};
export function LoginContainer(props) {
  const {
    handleLoginSuccess: parentHandleLoginSuccess,
    isLoggedIn,
    setIsLoggedIn,
  } = props;
  const [state, updateState] = useState(initState);

  const { isBarkoUi, isEnovationUi, isVersatileUi, terms_and_conditions_url } =
    useContext(BrandingContext);

  const api = useMemo(() => new RequestBuilder(), []),
    auth = useMemo(() => new AuthService(), []),
    localStorageService = useMemo(() => new LocalStorageService(), []);

  const history = useHistory(),
    location = useLocation();

  const handleLoginSuccess = useCallback(
    ({ token, user }) => {
      setIsLoggedIn(true);

      auth.setToken(token);
      auth.setUserRootOrgID(user.rootOrgID);
      auth.setCurrentOrgID(user.rootOrgID);
      localStorageService.setObject('permissions', user.permissions);

      parentHandleLoginSuccess();
      history.replace(location?.state?.targetPath ?? MAP);
    },
    [
      auth,
      history,
      localStorageService,
      location?.state?.targetPath,
      parentHandleLoginSuccess,
      setIsLoggedIn,
    ]
  );

  const handleAllTermsAccepted = useCallback(
      (loginRes) => {
        handleLoginSuccess(loginRes);
      },
      [handleLoginSuccess]
    ),
    handlePropUpdate = useCallback(({ target: { name, value } }) => {
      updateState((prevState) => ({
        ...prevState,
        [name]: value,
        showError: false,
      }));
    }, []),
    toggleAlert = useCallback(() => {
      updateState((prevState) => ({ ...prevState, showAlert: false }));
    }, []),
    togglePasswordType = useCallback(() => {
      updateState((prevState) => ({
        ...prevState,
        hidePassword: !prevState.hidePassword,
      }));
    }, []),
    showResetPasswordAlert = useCallback(
      (alertColor, alertText, alertMessage) => {
        updateState((prevState) => {
          window.setTimeout(toggleAlert, 8000);

          return {
            ...prevState,
            alertColor,
            alertMessage,
            alertText,
            showAlert: true,
            showError: false,
          };
        });
      },
      [toggleAlert]
    ),
    showTwoFactorAlert = useCallback(() => {
      updateState((prevState) => {
        const sentOrResent = prevState.attemptCount === 3 ? 'sent' : 'resent';
        window.setTimeout(toggleAlert, 8000);

        return {
          ...prevState,
          alertMessage: `6 digit token has been ${sentOrResent}!`,
          firstAttempt: false,
          showAlert: true,
          showError: false,
          showMFA: true,
        };
      });
    }, [toggleAlert]),
    toggleAcceptTermsAndConditionsModal = useCallback((loginRes = {}) => {
      updateState((prevState) => ({
        ...initState,
        loginRes,
        showAcceptTermsModal: !prevState.showAcceptTermsModal,
      }));
    }, []),
    toggleTermsAndConditionsModal = useCallback(() => {
      isBarkoUi || isEnovationUi || isVersatileUi
        ? window.open(terms_and_conditions_url, '_blank')
        : updateState((prevState) => ({
            ...prevState,
            termsModal: !prevState.termsModal,
          }));
    }, [isBarkoUi, isEnovationUi, isVersatileUi, terms_and_conditions_url]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    updateState((prevState) => ({ ...prevState, loginRequestPending: true }));

    try {
      const { email, password } = state;
      const { data } = await api.request(
        auth.buildLoginRequest({ email, password })
      );

      if (data?.hasAcceptedTerms?.every?.(({ accepted }) => accepted)) {
        handleLoginSuccess(data);
      } else {
        toggleAcceptTermsAndConditionsModal(data);
      }
    } catch (err) {
      const errorCode = err?.response?.status;
      const clientToken = err?.response?.data?.clientToken;
      const error =
        errorCode === 400
          ? 'Invalid email or password'
          : err?.response?.data?.message ??
            'We were not able to process your request. Please try again later.';
      updateState((prevState) => ({
        ...prevState,
        clientToken,
        error,
        loginRequestPending: false,
        showError: true,
      }));
      if (errorCode === 403) showTwoFactorAlert();
    }
  };

  const handleTwoFactor = async (e) => {
    e.preventDefault();
    updateState((prevState) => ({ ...prevState, loginRequestPending: true }));

    try {
      const { email, clientToken, challenge } = state;
      const { data } = await api.request(
        auth.buildTwoFactorRequest({ email, clientToken, challenge })
      );

      if (data?.hasAcceptedTerms?.every?.(({ accepted }) => accepted)) {
        handleLoginSuccess(data);
      } else {
        toggleAcceptTermsAndConditionsModal(data);
      }
    } catch (err) {
      const updatedCount = state.attemptCount - 1;
      if (updatedCount === 0) updateState(initState);
      const errorCode = err?.response?.status;
      const error =
        errorCode === 400 && updatedCount != 1
          ? `Incorrect verification code. ${updatedCount} tries remaining.`
          : errorCode === 400 && updatedCount === 1
            ? `Incorrect verification code. 1 try remaining.`
            : err?.response?.data?.message ??
              'We were not able to process your request. Please try again later.';
      if (updatedCount > 0) {
        updateState((prevState) => ({
          ...prevState,
          attemptCount: updatedCount,
          loginRequestPending: false,
          showError: true,
          twoFactorError: error,
        }));
      }
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    setIsLoggedIn(false);
  }, []);

  return (
    <>
      {!state.showMFA ? (
        <LoginDisplay
          {...state}
          handlePropUpdate={handlePropUpdate}
          handleSubmit={handleSubmit}
          togglePasswordType={togglePasswordType}
          toggleTermsAndConditionsModal={toggleTermsAndConditionsModal}
          showResetPasswordAlert={showResetPasswordAlert}
          toggleAlert={toggleAlert}
        />
      ) : (
        <MFADisplay
          {...state}
          handlePropUpdate={handlePropUpdate}
          handleSubmit={handleSubmit}
          handleTwoFactor={handleTwoFactor}
          toggleAlert={toggleAlert}
          toggleTermsAndConditionsModal={toggleTermsAndConditionsModal}
        />
      )}
      {state.showAcceptTermsModal ? (
        <AcceptTermsAndConditionsModal
          handleAllTermsAccepted={handleAllTermsAccepted}
          isOpen={state.showAcceptTermsModal}
          loginRes={state.loginRes}
          toggleModal={() => toggleAcceptTermsAndConditionsModal()}
        />
      ) : null}
    </>
  );
}

LoginContainer.propTypes = {
  handleLoginSuccess: PropTypes.func,
  isLoggedIn: PropTypes.bool,
  setIsLoggedIn: PropTypes.func,
};
